import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import { CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  TEAM_STAFF_CHART_COLORS,
  TEAM_STAFF_DETAIL_CHART_HEIGHT,
} from "./constants";
import type { TeamStaffDailyPoint } from "./staffDetailTypes";

function formatMinutes(value: number): string {
  const minutes = Math.floor(value);
  const seconds = Math.round((value - minutes) * 60);
  if (seconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
}

function TasksTimeTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number }>;
  label?: string;
  series: Array<{ key: string; label: string; color: string }>;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="grid min-w-[10rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        const raw = Number(item.value ?? 0);
        const formatted =
          item.dataKey === "avgTimeOnTaskMinutes"
            ? formatMinutes(raw)
            : raw.toLocaleString("en-US");

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">{formatted}</span>
          </div>
        );
      })}
    </div>
  );
}

interface TeamStaffTasksTimeChartProps {
  data: TeamStaffDailyPoint[];
}

export const TeamStaffTasksTimeChart: React.FC<
  TeamStaffTasksTimeChartProps
> = ({ data }) => {
  const { t } = useTranslation();

  const series = useMemo(
    () =>
      [
        {
          key: "tasksCompleted" as const,
          label: t("analytics.team.table.tasksCompleted", "Tasks completed"),
          color: TEAM_STAFF_CHART_COLORS.tasksCompleted,
        },
        {
          key: "avgTimeOnTaskMinutes" as const,
          label: t("analytics.team.table.avgTimeOnTask", "Avg time on task"),
          color: TEAM_STAFF_CHART_COLORS.avgTimeOnTask,
        },
      ] as const,
    [t],
  );

  const chartConfig = useMemo(
    () =>
      ({
        tasksCompleted: {
          label: t("analytics.team.table.tasksCompleted", "Tasks completed"),
          color: TEAM_STAFF_CHART_COLORS.tasksCompleted,
        },
        avgTimeOnTaskMinutes: {
          label: t("analytics.team.table.avgTimeOnTask", "Avg time on task"),
          color: TEAM_STAFF_CHART_COLORS.avgTimeOnTask,
        },
      }) satisfies ChartConfig,
    [t],
  );

  const maxTasks = Math.max(...data.map((point) => point.tasksCompleted), 10);
  const maxMinutes = Math.max(
    ...data.map((point) => point.avgTimeOnTaskMinutes),
    5,
  );
  const tasksYMax = Math.ceil(maxTasks / 5) * 5;
  const minutesYMax = Math.ceil(maxMinutes / 5) * 5;
  const chartHeight = TEAM_STAFF_DETAIL_CHART_HEIGHT;

  return (
    <RevenueChartCard
      title={t(
        "analytics.team.charts.tasksVsAvgTime",
        "Tasks completed vs Avg. time on task",
      )}
      icon={<ChartLine />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      style={{
        height: chartHeight,
        minHeight: chartHeight,
      }}
      className="flex flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <ChartContainer
          config={chartConfig}
          className="min-h-0 w-full flex-1 !aspect-auto"
        >
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 48, left: 0, bottom: 0 }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={4}
              className="text-xs text-muted-foreground"
            />
            <YAxis
              yAxisId="tasks"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={40}
              domain={[0, tasksYMax]}
              className="text-xs text-muted-foreground"
            />
            <YAxis
              yAxisId="minutes"
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={40}
              domain={[0, minutesYMax]}
              tickFormatter={(value) => `${value}m`}
              className="text-xs text-muted-foreground"
            />
            <ChartTooltip
              cursor={false}
              content={<TasksTimeTooltip series={[...series]} />}
            />
            <Line
              yAxisId="tasks"
              type="monotone"
              dataKey="tasksCompleted"
              stroke={TEAM_STAFF_CHART_COLORS.tasksCompleted}
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="minutes"
              type="monotone"
              dataKey="avgTimeOnTaskMinutes"
              stroke={TEAM_STAFF_CHART_COLORS.avgTimeOnTask}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ChartContainer>
        <p className="shrink-0 text-xs font-medium leading-normal text-[#737373]">
          {t("analytics.common.date", "Date")}
        </p>
        <RevenueChartLegend items={[...series]} />
      </div>
    </RevenueChartCard>
  );
};
